import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { FHButton } from '../../../../../components/base';
import { FInput } from '../../../../../components/baseV2';
import { Themes } from '../../../../../constants';
import { getThemeColor } from '../../../../../utils/common';
import DocumentUploadIcon from '../../../../../assets/images/document-upload.svg';
import CloseIcon from '../../../../../components/svgs/CloseIcon';
import { DOCUMENT_UPLOAD_ACCEPTED_TYPES, MIN_DOCUMENT_TITLE_LENGTH } from './constants';
import { FORTA_ERROR } from '../../../../../utils/colors';
import { DIALOG_TITLE_SX, getDropzoneSx } from './styles';
import {
  getFileRejectionMessage,
  getFileValidationError,
  isDuplicateFileName,
  toSelectedFile,
} from './utils';

import type { ChangeEvent } from 'react';
import type { FileRejection } from 'react-dropzone';
import type { DocumentUploadDialogProps, SelectedFile } from './types';

export default function DocumentUploadDialog({
  open,
  onClose,
  onSelect,
  title,
  subTitle = 'Document file must be a JPG, PNG, or PDF file',
  dropzoneHeading = 'Drag document here',
  dropzoneHint = 'or use the Browse button to select a document',
  submitText = 'Select Doc',
  accept = DOCUMENT_UPLOAD_ACCEPTED_TYPES,
  maxSizeMb = 10,
  noPreviewFiles = ['.pdf'],
  existingFileNames = [],
  defaultDocumentTitle = '',
  initialFile = null,
}: DocumentUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [documentTitle, setDocumentTitle] = useState(defaultDocumentTitle);
  const [fileError, setFileError] = useState('');
  const wasOpenRef = useRef(false);
  const seededInitialFileRef = useRef<File | null>(null);

  // The OS file browser always offers an "All files" escape hatch, so an unsupported or oversized
  // file still reaches the dropzone — it just lands in `rejections` instead of `files`. Surfacing
  // it here is what stops the pick from silently doing nothing.
  const handleDrop = useCallback(
    (files: File[], rejections: FileRejection[]) => {
      const tooManyFilesRejection = rejections.find(({ errors }) =>
        errors.some(({ code }) => code === 'too-many-files'),
      );
      if (tooManyFilesRejection) {
        setSelectedFile(null);
        setFileError(getFileRejectionMessage(tooManyFilesRejection, accept, maxSizeMb));
        return;
      }

      const [file] = files;
      if (!file) {
        const [rejection] = rejections;
        setSelectedFile(null);
        setFileError(rejection ? getFileRejectionMessage(rejection, accept, maxSizeMb) : '');
        return;
      }

      if (isDuplicateFileName(file.name, existingFileNames)) {
        setSelectedFile(null);
        setFileError(
          `"${file.name}" has already been uploaded. Rename the file or choose a different one.`,
        );
        return;
      }

      setFileError('');
      setSelectedFile(toSelectedFile(file, noPreviewFiles));
      // Referral seeds the editable title from the file name (no defaultDocumentTitle). When Browse
      // replaces that file, refresh the auto title so submit/min-length checks track the new name.
      // Insurance supplies a fixed defaultDocumentTitle and should keep whatever is in the field.
      if (!defaultDocumentTitle) {
        setDocumentTitle(file.name);
      }
    },
    [accept, defaultDocumentTitle, existingFileNames, maxSizeMb, noPreviewFiles],
  );

  const {
    getRootProps,
    getInputProps,
    open: openFileBrowser,
  } = useDropzone({
    accept,
    maxFiles: 1,
    multiple: false,
    maxSize: maxSizeMb * 1024 * 1024,
    noPaste: true,
    onDrop: handleDrop,
  });

  useEffect(() => {
    return () => {
      if (selectedFile?.preview) {
        URL.revokeObjectURL(selectedFile.preview);
      }
    };
  }, [selectedFile]);

  // Seed on open / when a capture arrives — not when parent array identities churn.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;

    if (!open) {
      if (wasOpen) {
        setSelectedFile(null);
        setDocumentTitle('');
        setFileError('');
        seededInitialFileRef.current = null;
      }
      return;
    }

    const justOpened = !wasOpen;
    const initialFileChanged = initialFile !== seededInitialFileRef.current;
    if (!justOpened && !initialFileChanged) {
      return;
    }

    seededInitialFileRef.current = initialFile;

    if (!initialFile) {
      setSelectedFile(null);
      setDocumentTitle(defaultDocumentTitle);
      setFileError('');
      return;
    }

    const validationError = getFileValidationError(initialFile, accept, maxSizeMb);
    if (validationError) {
      setSelectedFile(null);
      setDocumentTitle(defaultDocumentTitle);
      setFileError(validationError);
      return;
    }

    if (isDuplicateFileName(initialFile.name, existingFileNames)) {
      setSelectedFile(null);
      setDocumentTitle(defaultDocumentTitle);
      setFileError(
        `"${initialFile.name}" has already been uploaded. Rename the file or choose a different one.`,
      );
      return;
    }

    setFileError('');
    setSelectedFile(toSelectedFile(initialFile, noPreviewFiles));
    // Referral has no default title — seed the editable field with the captured file name so a
    // short camera name surfaces the min-length error immediately.
    setDocumentTitle(defaultDocumentTitle || initialFile.name);
  }, [
    accept,
    defaultDocumentTitle,
    existingFileNames,
    initialFile,
    maxSizeMb,
    noPreviewFiles,
    open,
  ]);

  const trimmedDocumentTitle = documentTitle.trim();
  const resolvedDocumentTitle = trimmedDocumentTitle || selectedFile?.name || '';
  const isDocumentTitleTooShort =
    resolvedDocumentTitle.length > 0 &&
    resolvedDocumentTitle.length < MIN_DOCUMENT_TITLE_LENGTH;

  const handleSubmit = () => {
    if (!selectedFile || isDocumentTitleTooShort) {
      return;
    }

    // The stored document is keyed by title, so a typed-in title can collide even when the
    // selected file's name did not.
    if (isDuplicateFileName(resolvedDocumentTitle, existingFileNames)) {
      setFileError(
        `A document titled "${resolvedDocumentTitle}" already exists. Enter a different title.`,
      );
      return;
    }

    onSelect(selectedFile, resolvedDocumentTitle);
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // A rejected drop always clears the selection, so an error shown while a file is still
    // selected can only be a title collision — which editing the title may well resolve.
    if (selectedFile) {
      setFileError('');
    }
    setDocumentTitle(event.target.value);
  };

  let documentTitleHelperText: string | undefined;
  if (isDocumentTitleTooShort) {
    if (
      trimmedDocumentTitle.length > 0 &&
      (selectedFile?.name.length ?? 0) >= MIN_DOCUMENT_TITLE_LENGTH
    ) {
      documentTitleHelperText = `Enter at least ${MIN_DOCUMENT_TITLE_LENGTH} characters or clear the field`;
    } else {
      documentTitleHelperText = `Enter at least ${MIN_DOCUMENT_TITLE_LENGTH} characters`;
    }
  }

  const primaryColor = getThemeColor(Themes.Primary).primary;
  const lightBackground = getThemeColor(Themes.Primary).light3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(2px)' },
        },
      }}
      PaperProps={{ sx: { borderRadius: '10px' } }}
    >
      <DialogTitle sx={DIALOG_TITLE_SX}>
        <Box>
          <Typography variant="h6" fontWeight="bold" align="left">
            {title}
          </Typography>
          {subTitle ? <Typography align="left">{subTitle}</Typography> : null}
        </Box>
        <IconButton sx={{ p: 0 }} disableRipple onClick={onClose}>
          <CloseIcon color={primaryColor} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box
            {...getRootProps({ className: 'dropzone' })}
            sx={getDropzoneSx(primaryColor, lightBackground)}
          >
            <input {...getInputProps()} />
            {selectedFile?.preview ? (
              <Box
                alt="preview"
                component="img"
                src={selectedFile.preview}
                sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : selectedFile?.noPreview ? (
              <>
                <Typography fontWeight="bold">Preview not available for:</Typography>
                <Typography fontWeight="bold">{selectedFile.name}</Typography>
              </>
            ) : (
              <>
                <Box
                  alt="upload"
                  component="img"
                  src={DocumentUploadIcon}
                  sx={{ height: 120, width: 120 }}
                />
                <Typography variant="h6">{dropzoneHeading}</Typography>
                <Typography>{dropzoneHint}</Typography>
              </>
            )}
          </Box>
          {fileError ? (
            <Typography role="alert" sx={{ color: FORTA_ERROR }} variant="body2">
              {fileError}
            </Typography>
          ) : null}
          <Stack alignItems="center" direction="row" gap={2}>
            <FInput
              disabled
              fullWidth
              label="File name"
              name="file_name"
              placeholder="No file selected"
              value={selectedFile?.name ?? ''}
            />
            <FHButton colorTheme={Themes.Primary} onClick={openFileBrowser}>
              Browse
            </FHButton>
          </Stack>
          <FInput
            error={isDocumentTitleTooShort}
            fullWidth
            helperText={documentTitleHelperText}
            label="Document title"
            name="document_title"
            onChange={handleTitleChange}
            placeholder="Enter document title"
            value={documentTitle}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FHButton colorTheme={Themes.Primary} onClick={onClose} secondary>
          Cancel
        </FHButton>
        <FHButton
          colorTheme={Themes.Primary}
          disabled={selectedFile == null || isDocumentTitleTooShort}
          onClick={handleSubmit}
        >
          {submitText}
        </FHButton>
      </DialogActions>
    </Dialog>
  );
}
